/* general: the shared components, the ⌘K search, page titles, the add-client
 * form, help and not-found — the Wave 3 translation pass (2026-09-30).
 *
 * Spread into the main catalogue, so every top-level key here is a namespace
 * of its own: `ui`, `search`, `route`, `addClient`, `help`, `notFound`. Words
 * the rest of the product already has (`common.*`, `states.*`, `nav.*`,
 * `clients.*`, `opp.*`) are reused from there rather than repeated here. */
export default {
  /* Built-in words of the shared components (`components/ui`, `layout`,
   * `tables/Pagination`). Text a page passes in through a prop is the page's. */
  ui: {
    breadcrumb: 'Breadcrumb',
    back: 'Back',
    updated: 'Updated {when}',
    confirm: {
      title: 'Are you sure?',
      message: 'This action cannot be undone.',
    },
    chips: {
      remove: 'Remove {label}',
    },
    filters: {
      title: 'Filters',
      close: 'Close filters',
    },
    /* The counter after the number: "3 trademarks selected". One message per
     * noun, because a noun cannot be pluralised by adding a letter in any of
     * the other three languages. */
    bulk: {
      trademark: 'trademark selected | trademarks selected',
      case: 'case selected | cases selected',
      client: 'client selected | clients selected',
      match: 'match selected | matches selected',
      item: 'item selected | items selected',
      selected: 'selected',
      clear: 'Clear selection',
    },
    pagination: {
      showing: 'Showing {range} of {total} {unit}',
      items: 'items',
      prev: 'Previous page',
      next: 'Next page',
    },
    toast: {
      region: 'Status messages',
    },
    error: {
      offlineTitle: 'You appear to be offline',
      offline: 'Check your connection and try again.',
      timeoutTitle: 'The request timed out',
      timeout: 'The server took too long to answer. Try again in a moment.',
      authTitle: 'Your session has expired',
      auth: 'Sign in again to continue; you will come back to this page.',
      rateTitle: 'Too many requests',
      rate: 'Give it a few seconds and try again.',
      serverTitle: 'The server is having trouble',
      generic: 'An unexpected error happened. Try again in a moment.',
      render: 'An unexpected error happened while showing this section.',
    },
    notFound: {
      title: {
        item: 'Not found',
        client: 'Client not found',
        trademark: 'Trademark not found',
        report: 'Report not found',
        case: 'Opposition case not found',
      },
      sub: 'It may have been deleted, the link may be out of date, or your account may not have access to it.',
    },
  },

  /* The ⌘K search. */
  search: {
    label: 'Search Lexentiqa',
    placeholder: 'Search trademarks, clients and cases…',
    hint: 'Type at least two letters to search trademarks, clients and opposition cases.',
    searching: 'Searching…',
    noResults: 'No results for “{q}”. Try a trademark name, a client or a case number.',
    failed: 'Search is not available right now. Try again in a moment.',
    navigate: 'navigate',
    open: 'open',
    close: 'close',
    anywhere: 'anywhere',
  },

  /* The browser tab's title, one per named route (the name in camelCase). */
  route: {
    dashboard: 'Dashboard',
    monitoring: 'TM Monitoring',
    matchDetail: 'Match review',
    trademarks: 'My Trademarks',
    trademarkNew: 'Add trademark',
    trademarkDetail: 'Trademark dossier',
    oppositions: 'Opposition cases',
    oppositionNew: 'New opposition case',
    oppositionDetail: 'Opposition case',
    reports: 'Reports',
    reportDetail: 'Scan report',
    clients: 'Clients',
    clientNew: 'Add client',
    clientDetail: 'Client',
    people: 'People',
    personNew: 'Add a person',
    person: 'Person',
    clientEdit: 'Edit client',
    notifications: 'Notifications',
    help: 'Help center',
    howItWorks: 'How the monitoring works',
    roadmap: 'What we’re building',
    account: 'Account',
    settings: 'Settings',
    styleguide: 'Styleguide',
    signIn: 'Sign in',
    forgotPassword: 'Reset your password',
    setPassword: 'Set your password',
    notFound: 'Not found',
  },

  /* The add-client form (W3.M6). What a select stores is the label in the
   * language of the person who chose it: `clients.industry`, `legal_form` and
   * `company_size` are free text that the dossier prints as stored. */
  addClient: {
    subtitle:
      'Add a new trademark owner to the workspace. Monitoring is set per trademark, once their marks are added.',
    create: 'Create client',
    creating: 'Creating…',
    workspace: 'Add client workspace',
    required: 'Required',
    company: {
      title: 'Company details',
      sub: 'The legal entity behind the trademarks.',
      name: 'Legal name',
      namePlaceholder: 'e.g. Example Ltd',
      nameHint: 'The full name as entered in the commercial register.',
      nameRequired: 'Enter the client’s legal name.',
      nameShort: 'The name needs at least {n} characters.',
      tradingName: 'Trading name',
      tradingNamePlaceholder: 'If different from the legal name',
      legalForm: 'Legal entity type',
      legalFormPlaceholder: 'Choose the entity type…',
      registrationNumber: 'Registration number',
      registrationNumberPlaceholder: 'EIK / company №',
      founded: 'Founded',
      foundedPlaceholder: 'YYYY',
      industry: 'Industry',
      industryPlaceholder: 'Choose the industry…',
      size: 'Company size',
      sizePlaceholder: 'Choose the size…',
      website: 'Website',
    },
    legalForm: {
      ood: 'OOD (Bulgarian Ltd)',
      eood: 'EOOD (Bulgarian single-member Ltd)',
      ad: 'AD (Bulgarian JSC)',
      ead: 'EAD (Bulgarian single-member JSC)',
      et: 'ET (Bulgarian sole trader)',
      gmbh: 'GmbH (Germany, Austria)',
      sa: 'S.A. / SARL (France and others)',
      inc: 'Inc. / Corp. (US)',
      llc: 'LLC (US)',
      other: 'Other',
    },
    industry: {
      software: 'Software / SaaS',
      finance: 'Financial services',
      manufacturing: 'Industrial manufacturing',
      cosmetics: 'Beauty / cosmetics',
      pharma: 'Pharma / medical',
      biotech: 'Biotech',
      beverages: 'Beverages',
      fashion: 'Fashion / apparel',
      telecom: 'Telecommunications',
      other: 'Other',
    },
    size: {
      s1: '1–10 employees',
      s2: '11–50 employees',
      s3: '51–200 employees',
      s4: '201–500 employees',
      s5: '501–1,000 employees',
      s6: '1,000+ employees',
    },
    hq: {
      title: 'Headquarters',
      sub: 'Where the company itself is registered. The registries its trademarks are watched on are chosen per trademark, not here.',
      country: 'Country / region',
      countryPlaceholder: 'Choose the country…',
    },
    contact: {
      title: 'Primary contact',
      sub: 'Who to contact at the client company. Stored on the client record.',
      name: 'Primary contact name',
      namePlaceholder: 'Full name',
      nameRequired: 'Enter the primary contact’s name.',
      email: 'Email',
      emailRequired: 'Enter the contact’s email address.',
      emailInvalid: 'Enter a valid email address.',
    },
    notes: {
      title: 'Internal notes',
      sub: 'Context for your own team. Never shown to the client.',
      label: 'Notes',
      placeholder:
        'How the relationship started, who introduced them, anything the next person picking this up should know.',
    },
    rail: {
      label: 'Onboarding context',
      checklist: 'Onboarding checklist',
      step: 'Step {n}.',
      step1: 'Fill in the company details and headquarters.',
      step2: 'Record who to contact at the client company.',
      step3:
        'After creating the client, add their trademarks — each mark carries its own monitoring scope.',
      done: 'Done',
    },
    actions: 'Form actions',
    notSaved: 'Nothing is saved until you create the client.',
    incomplete: 'Please complete the required fields',
    created: 'Client created',
    createdSub: '{name} added to your client list.',
    failed: 'The client was not created',
  },

  /* The help page. Every answer is checked against what the product does
   * today (2026-09-30); when one of them stops being true, change it here. */
  help: {
    title: 'Help center',
    subtitle: 'How the product works, where to find things, and short answers to common questions.',
    topics: 'Help topics',
    topic: {
      howItWorks: 'How the monitoring works',
      howItWorksSub: 'From a registry bulletin to a conflict on your desk, step by step',
      roadmap: 'What we’re building, and where it stands',
      roadmapSub:
        'The full development plan in plain English — finished, under way, and still ahead',
      trademarksSub:
        'Your clients’ marks, the registries each one is watched on, and their documents',
      monitoringSub:
        'What the monitoring found against your clients’ marks, and how each pair was decided',
    },
    faq: {
      title: 'Frequently asked questions',
      sub: 'Short answers about how the product works today.',
      bulletins: {
        q: 'How often are new bulletins collected?',
        a: 'The platform checks EUIPO and WIPO for new bulletins every three hours and reads each one once it has been collected. National registries, the Bulgarian office included, are not read yet. Comparing the new filings with your trademarks is a separate step, a scan, which the platform operator runs; every day a scan ran has its own report under Reports.',
      },
      sensitivity: {
        q: 'Can we change how sensitive the matching is?',
        a: 'Not per firm. The matching is tuned for the whole platform by its operator, against a recall target: the share of trademark pairs actually opposed at EUIPO that the matcher must bring before a lawyer, measured on EUIPO’s own opposition records. Every match says how it was decided — by a matching rule, or read by the AI reviewer, with its reasons.',
      },
      clientAccess: {
        q: 'Can a client have its own read-only access?',
        a: 'Yes. A client account is read-only. It sees only the open opposition cases on its own trademarks, and within them only the steps, notes and documents the firm has marked as visible to the client, plus the statutory deadlines. It never sees the monitoring or its matches. Adding people and choosing what each of them can see is not in the app yet: ask the platform operator.',
      },
      deadlines: {
        q: 'Where do case deadlines come from?',
        a: 'A case opened from a match starts with the found filing’s own opposition deadline, taken from the registry’s publication. Where the rule behind that date has not yet been confirmed by the firm’s lawyers — every WIPO date, and EUIPO republications of international registrations — it is marked provisional: check it before relying on it. Any other deadline is added on the case page by a lawyer. The assigned lawyer (or, if nobody is assigned, the firm’s main account) is reminded a week before, the day before, on the day and once it has passed — in the app, and by email when the platform sends email and they have not switched it off in Settings.',
      },
      data: {
        q: 'Where is our data kept?',
        a: 'The database and every uploaded file are kept on the platform’s own server, in a data centre in Germany, inside the EU — not with a cloud storage service. The database is copied every night and each copy is kept for up to 30 days; the server’s whole disk is backed up as well. The AI work runs on our own graphics hardware in Bulgaria: it is sent the trademarks to compare and sends back its answer. When a close call is passed to the paid AI model (Anthropic’s Claude), it receives the two trademarks being compared — their names, their goods and services, and a written description of any logo — and no client names or files.',
      },
      search: {
        q: 'How do I find a trademark, client or case quickly?',
        a: 'Press ⌘K (Ctrl+K on Windows) on any page, or click the search box at the top, and type at least two letters of a trademark, a client, or a case title or number.',
      },
    },
  },

  notFound: {
    sub: 'The page you are looking for does not exist or has been moved.',
    back: 'Back to the workspace',
  },
} as const
